// CRM: lifetime value. Realised = invoices (cancelled excluded); predicted = what the child is still likely to
// spend, from their age. Pure; see crmTypes.ts and docs/crm-design.md for the formulas.
//
// Per child:
//   preschool  = remaining terms until school (September after turning 4) x weeks per term x weekly fee x retention
//   holiday    = years left until 31 Aug after Year 6 (from age 3) x yearly holiday spend x retention
//                yearly spend = the child's own (holiday invoiced / years since first booking, at least 1), or for a
//                child who never booked: average yearly spend of booking children x take-up rate
//   retention  = by stage (an alumni family is worth nothing more, a lapsed one a quarter, ...)

import type { CrmChild, CrmPreschoolInvoice, Identity, LtvLine, Prediction, Stage } from "./crmTypes";
import { holidayUntil } from "./crmLifecycle";
import {
  addYears,
  compareTermIds,
  entitlementHours,
  formatPounds,
  formatPoundsExact,
  isTermId,
  monthYear,
  nextTermId,
  schoolStartYear,
  sessionsPerWeek,
  termIdFor,
  termName,
  termSeason,
  termsBetween,
  yearsBetween,
} from "./crmNormalise";

export interface LtvAssumptions {
  /** Chargeable weeks per term (38 a year), as preschoolInvoicing.ts defaults. */
  termWeeks: { autumn: number; spring: number; summer: number };
  hoursPerSession: number;
  privateRatePence: number;
  consumablePence: number;
  /** Holiday Club takes children from this age. */
  holidayMinAge: number;
  /** Years of Holiday Club left for a child whose age we do not know. */
  unknownAgeHolidayYears: number;
  /** Terms of preschool left for an active child whose date of birth we do not know. */
  unknownDobPreschoolTerms: number;
  /** Used until there is enough data (see PopulationStats). */
  defaultHolidayYearPence: number;
  defaultTakeUp: number;
  defaultWeeklyPence: number;
  /** Share of the forecast a family in this stage is expected to realise. */
  retention: Record<Stage, number>;
  /** For "value of a new child by age" (Insights): typical ages at school start and at the end of Year 6. */
  typicalSchoolStartAge: number;
  typicalHolidayEndAge: number;
  lapseMonths: number;
  registrationValidMonths: number;
}

export const DEFAULT_ASSUMPTIONS: LtvAssumptions = {
  termWeeks: { autumn: 14, spring: 11, summer: 13 },
  hoursPerSession: 3,
  privateRatePence: 900,
  consumablePence: 280,
  holidayMinAge: 3,
  unknownAgeHolidayYears: 3,
  unknownDobPreschoolTerms: 3,
  defaultHolidayYearPence: 28000,
  defaultTakeUp: 0.25,
  defaultWeeklyPence: 6000,
  retention: { active: 1, registered: 0.9, holidayOnly: 1, enquiry: 0.3, lapsed: 0.25, alumni: 0 },
  typicalSchoolStartAge: 4.5,
  typicalHolidayEndAge: 11.5,
  lapseMonths: 12,
  registrationValidMonths: 6,
};

export const termWeeks = (termId: string, a: LtvAssumptions) => {
  const season = termSeason(termId);
  return season ? a.termWeeks[season] : 0;
};

/**
 * The weekly preschool fee to forecast with: the latest term invoice spread over its weeks, else an estimate from
 * the enrolment (private hours at the hourly rate, funded hours at the consumables rate).
 */
export const weeklyFee = (
  invoices: CrmPreschoolInvoice[],
  identities: Identity[],
  a: LtvAssumptions
): CrmChild["weekly"] => {
  const termInvoices = invoices
    .filter((i) => !i.cancelled && i.kind === "term" && isTermId(i.termId) && i.totalPence > 0)
    .sort((x, y) => compareTermIds(x.termId!, y.termId!) || x.issuedDate.localeCompare(y.issuedDate));
  const latest = termInvoices[termInvoices.length - 1];
  if (latest) {
    const weeks = termWeeks(latest.termId!, a);
    return {
      pence: Math.round(latest.totalPence / weeks),
      basis: "invoice",
      detail: `${latest.ref}: ${formatPoundsExact(latest.totalPence)} over ${weeks} weeks`,
    };
  }
  const p = identities.map((i) => i.preschool).find((x) => x && sessionsPerWeek(x.enrolment) > 0);
  if (p) {
    const hours = sessionsPerWeek(p.enrolment) * a.hoursPerSession;
    const funded = Math.min(hours, entitlementHours(p.funding));
    const pence = (hours - funded) * a.privateRatePence + funded * a.consumablePence;
    return {
      pence,
      basis: "enrolment",
      detail: `${hours} h a week (${funded} funded) at ${formatPoundsExact(a.privateRatePence)}/h private, ` +
        `${formatPoundsExact(a.consumablePence)}/h consumables`,
    };
  }
  return null;
};

export interface PopulationStats {
  /** Average yearly Holiday Club spend of children who booked. */
  holidayYearPence: number;
  holidayChildren: number;
  /** Share of preschool children who also booked Holiday Club. */
  takeUp: number;
  preschoolChildren: number;
  /** Average weekly preschool fee from term invoices. */
  weeklyPence: number;
  weeklyChildren: number;
  fromData: { holiday: boolean; takeUp: boolean; weekly: boolean };
}

/** The child's own yearly Holiday Club spend: holiday invoiced / years since the first booking (at least one year). */
export const ownHolidayYearPence = (holidayInvoicedPence: number, firstHoliday: string | null, today: string) =>
  holidayInvoicedPence > 0 ? Math.round(holidayInvoicedPence / Math.max(1, firstHoliday ? yearsBetween(firstHoliday, today) : 1)) : 0;

type ChildForStats = Pick<CrmChild, "preschoolStatus" | "holidayBookings" | "firstHoliday" | "holiday" | "weekly">;

/** Averages that stand in for a child's own history. Defaults apply until at least 5 children back them. */
export const populationStats = (children: ChildForStats[], today: string, a: LtvAssumptions): PopulationStats => {
  const holidayRates = children.map((c) => ownHolidayYearPence(c.holiday.invoicedPence, c.firstHoliday, today)).filter((p) => p > 0);
  const preschool = children.filter((c) => c.preschoolStatus === "active" || c.preschoolStatus === "left");
  const both = preschool.filter((c) => c.holidayBookings > 0);
  const weekly = children.map((c) => c.weekly).filter((w): w is NonNullable<typeof w> => !!w && w.basis === "invoice").map((w) => w.pence);
  const mean = (values: number[]) => Math.round(values.reduce((s, v) => s + v, 0) / values.length);
  const enough = (n: number) => n >= 5;
  return {
    holidayYearPence: enough(holidayRates.length) ? mean(holidayRates) : a.defaultHolidayYearPence,
    holidayChildren: holidayRates.length,
    takeUp: enough(preschool.length) ? both.length / preschool.length : a.defaultTakeUp,
    preschoolChildren: preschool.length,
    weeklyPence: enough(weekly.length) ? mean(weekly) : a.defaultWeeklyPence,
    weeklyChildren: weekly.length,
    fromData: { holiday: enough(holidayRates.length), takeUp: enough(preschool.length), weekly: enough(weekly.length) },
  };
};

const pct = (x: number) => `${Math.round(x * 100)}%`;
const years = (x: number) => `${x.toFixed(1)} yr${x.toFixed(1) === "1.0" ? "" : "s"}`;

/**
 * Forecast for one child. `stage` is the stage used for retention: the family's manual override if there is one,
 * otherwise the child's own stage.
 */
export const predictChild = (
  child: Pick<CrmChild, "name" | "dateOfBirth" | "preschoolStatus" | "holidayBookings" | "firstHoliday" | "holiday" | "weekly">,
  context: { stage: Stage; startDate: string | null; invoicedTerms: string[] },
  population: PopulationStats,
  today: string,
  a: LtvAssumptions
): Prediction => {
  const lines: LtvLine[] = [];
  const retention = a.retention[context.stage];
  const retentionText = retention === 1 ? "" : ` × ${pct(retention)} (${context.stage})`;
  const dob = child.dateOfBirth;

  let preschoolPence = 0;
  if (child.preschoolStatus === "active" || child.preschoolStatus === "registered") {
    const current = termIdFor(today);
    let first = context.startDate && context.startDate > today ? termIdFor(context.startDate) : current;
    if (first === current && context.invoicedTerms.includes(current)) first = nextTermId(current);
    const terms: string[] = [];
    if (dob) terms.push(...termsBetween(first, `${schoolStartYear(dob)}-summer`));
    else for (let id = first; terms.length < a.unknownDobPreschoolTerms; id = nextTermId(id)) terms.push(id);
    const weeks = terms.reduce((sum, id) => sum + termWeeks(id, a), 0);
    const weekly = child.weekly ?? { pence: population.weeklyPence, basis: "average" as const, detail: "average weekly fee" };
    preschoolPence = Math.round(weeks * weekly.pence * retention);
    if (terms.length) {
      lines.push({
        label: `${child.name} · preschool`,
        pence: preschoolPence,
        detail: `${terms.length} term${terms.length === 1 ? "" : "s"} (${termName(terms[0]!)} – ${termName(terms[terms.length - 1]!)}, ` +
          `${weeks} weeks) × ${formatPoundsExact(weekly.pence)}/week (${weekly.basis === "invoice" ? weekly.detail : weekly.basis === "enrolment" ? `estimate: ${weekly.detail}` : "average"})` +
          `${dob ? "" : `; date of birth unknown, assumed ${a.unknownDobPreschoolTerms} terms`}${retentionText}`,
      });
    }
  }

  let holidayYears: number;
  let window: string;
  if (dob) {
    const from = [today, addYears(dob, a.holidayMinAge)].sort()[1]!;
    const until = holidayUntil(dob);
    holidayYears = Math.max(0, yearsBetween(from, until));
    window = holidayYears > 0 ? `${years(holidayYears)} left (to ${monthYear(until)})` : "aged out";
  } else {
    holidayYears = a.unknownAgeHolidayYears;
    window = `age unknown, assumed ${years(holidayYears)}`;
  }
  const own = ownHolidayYearPence(child.holiday.invoicedPence, child.firstHoliday, today);
  const yearly = own > 0 ? own : Math.round(population.holidayYearPence * population.takeUp);
  const basis = own > 0
    ? "own bookings"
    : `average ${formatPounds(population.holidayYearPence)}/yr × ${pct(population.takeUp)} take-up`;
  const holidayPence = Math.round(holidayYears * yearly * retention);
  if (holidayYears > 0 || child.holidayBookings > 0) {
    lines.push({
      label: `${child.name} · holiday club`,
      pence: holidayPence,
      detail: `${window} × ${formatPounds(yearly)}/yr (${basis})${retentionText}`,
    });
  }
  return { preschoolPence, holidayPence, lines };
};

export interface AgeValue {
  label: string;
  preschoolPence: number;
  holidayPence: number;
  totalPence: number;
  detail: string;
}

/** What a new child is worth by the age they join, with typical school ages and the population averages. */
export const valueByStartingAge = (population: PopulationStats, a: LtvAssumptions): AgeValue[] => {
  const weeksPerYear = a.termWeeks.autumn + a.termWeeks.spring + a.termWeeks.summer;
  const holidayYearly = population.holidayYearPence;
  const rows: AgeValue[] = [];
  for (const age of [2, 2.5, 3, 3.5, 4]) {
    const preschoolYears = Math.max(0, a.typicalSchoolStartAge - age);
    const preschoolPence = Math.round(preschoolYears * weeksPerYear * population.weeklyPence);
    const holidayYears = a.typicalHolidayEndAge - Math.max(age, a.holidayMinAge);
    const holidayPence = Math.round(holidayYears * holidayYearly * population.takeUp);
    rows.push({
      label: `Preschool from ${age}`,
      preschoolPence,
      holidayPence,
      totalPence: preschoolPence + holidayPence,
      detail: `${preschoolYears} yrs × ${weeksPerYear} wks × ${formatPounds(population.weeklyPence)}; ` +
        `${holidayYears} yrs holiday × ${formatPounds(holidayYearly)} × ${pct(population.takeUp)}`,
    });
  }
  for (const age of [4, 6, 8, 10]) {
    const holidayYears = Math.max(0, a.typicalHolidayEndAge - age);
    const holidayPence = Math.round(holidayYears * holidayYearly);
    rows.push({
      label: `Holiday Club from ${age}`,
      preschoolPence: 0,
      holidayPence,
      totalPence: holidayPence,
      detail: `${holidayYears} yrs × ${formatPounds(holidayYearly)}/yr`,
    });
  }
  return rows;
};
